/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {GroupChooserIcon} from '../../../icons/current/GroupChooserIcon';
import {LocationPinIcon} from '../../../icons/current/LocationPinIcon';
import {RouteIcon} from '../../../icons/current/RouteIcon';
import {SystemDriverFilledIcon} from '../../../icons/current/SystemDriverFilledIcon';
import Render from '../../../render/Render';
import {dlsColor} from '../../../theme/theme';
import {IconSize} from '../../../types/types';
import {LinkedTextButton} from '../../buttons/current/LinkedTextButton';
import {ClampedTextWithTooltip} from './ClampedTextWithTooltip';

interface AssetSummaryCardBodyProps {
  location?: string;
  group?: string;
  route?: string;
  driver?: string;
  onDriverClick?: () => void;
}

const useStyles = makeStyles(() => ({
  body : {
    display       : 'flex',
    width         : '100%',
    flexDirection : 'column',
    gap           : '8px'
  },
  row : {
    display    : 'flex',
    width      : '100%',
    alignItems : 'center',
    gap        : '8px'
  },
  icon : {
    display        : 'flex',
    flexShrink     : 0,
    alignItems     : 'center',
    justifyContent : 'center'
  },
  text : {
    flex          : '1 0 0',
    minWidth      : 0,
    fontFamily    : 'Inter',
    fontWeight    : 600,
    fontSize      : '13px',
    lineHeight    : '16px',
    letterSpacing : '0.5px',
    color         : dlsColor('grey2')
  },
  driverText : {
    textTransform : 'capitalize'
  },
  // a driver that links to a driver card - the only driver drawn in Link
  // Text Blue ...
  driverButton : {
    textTransform   : 'capitalize',
    display         : '-webkit-box',
    overflow        : 'hidden',
    textAlign       : 'left',
    WebkitBoxOrient : 'vertical',
    WebkitLineClamp : 1,
    whiteSpace      : 'normal',
    height          : 'auto !important',
    paddingLeft     : '0 !important',
    fontFamily      : 'Inter',
    fontSize        : '13px',
    lineHeight      : '14px'
  }
}));

/**
 * AssetSummaryCardBody renders the card's Location/Group/Route/Driver rows,
 * each with a leading icon, per ION-7396. Location and Group wrap up to 2
 * lines and truncate with a tooltip beyond that (desktop only); Route and
 * Driver are single line only. Driver renders as plain text like Route, and
 * in Link Text Blue only when it links to a driver card - that is, only
 * when `onDriverClick` is given. Every row is omitted (not left blank) when
 * unavailable, contracting the section - each row is simply not rendered
 * when its value is falsy, so the flex column never leaves empty space.
 *
 * ```typescript
 *
 *   import {AssetSummaryCardBody} from
 *     '@ion/dls/src/components/assetcard/current/AssetSummaryCardBody';
 *   ...
 *
 *   <AssetSummaryCardBody location="13505 Zachary Taylor Dr, Manassas, Virginia"
 *     group="STREETS & STREETS ROADSIDE VEGETATION"
 *     route="Route Name"
 *     driver="Thomas Thumb"
 *     onDriverClick={onDriverClick} />
 *
 * ```
 *
 */
export const AssetSummaryCardBody = ({
  location,
  group,
  route,
  driver,
  onDriverClick
}: AssetSummaryCardBodyProps) => {
  const classes = useStyles();

  return (
    <div className={classes.body}>
      {location && (
        <div className={classes.row}>
          <div className={classes.icon}>
            <LocationPinIcon size={IconSize.small} color={dlsColor('grey2')} />
          </div>
          <ClampedTextWithTooltip className={classes.text}>
            {location}
          </ClampedTextWithTooltip>
        </div>
      )}
      {group && (
        <div className={classes.row}>
          <div className={classes.icon}>
            <GroupChooserIcon size={IconSize.small} color={dlsColor('grey2')} />
          </div>
          <ClampedTextWithTooltip className={classes.text}>
            {group}
          </ClampedTextWithTooltip>
        </div>
      )}
      {route && (
        <div className={classes.row}>
          <div className={classes.icon}>
            <RouteIcon size={IconSize.small} color={dlsColor('grey2')} />
          </div>
          <ClampedTextWithTooltip className={classes.text} lines={1}>
            {route}
          </ClampedTextWithTooltip>
        </div>
      )}
      {driver && (
        <div className={classes.row}>
          <div className={classes.icon}>
            <SystemDriverFilledIcon size={IconSize.small} color={dlsColor('grey2')} />
          </div>
          {/* a link only where it opens a driver card, plain text otherwise ... */}
          <Render>
            <Render.If condition={!!onDriverClick}>
              <LinkedTextButton className={classes.driverButton} onClick={onDriverClick}>
                {driver}
              </LinkedTextButton>
            </Render.If>
            <Render.Else>
              <ClampedTextWithTooltip className={classnames(classes.text, classes.driverText)} lines={1}>
                {driver}
              </ClampedTextWithTooltip>
            </Render.Else>
          </Render>
        </div>
      )}
    </div>
  );
};

export default AssetSummaryCardBody;
